import { useCallback, useRef } from 'react';
import {
  FormChecklistTemplate,
  FormChecklistTemplateFilterInputObject,
  FormChecklistTemplatesQueryVariables,
  useFormChecklistTemplateIdsLazyQuery,
  useFormChecklistTemplatesQuery,
} from 'graphql-common';
import _get from 'lodash/get';
import { GetUrlParams } from '@lib/enums/urls';
import createSearchFilters, {
  defaultSearchListSchema,
} from '@lib/utils/createSearchFilters';
import { ListQueryHookProps } from '../types';

export default function useFormChecklistTemplatesQueryHook({
  paginationParams,
  searchQuery,
}: ListQueryHookProps) {
  const page = _get(paginationParams, GetUrlParams.Page);
  const perPage = _get(paginationParams, GetUrlParams.PerPage);
  const orderingField = _get(paginationParams, GetUrlParams.OrderingField);
  const orderingDirection = _get(
    paginationParams,
    GetUrlParams.OrderingDirection,
  );
  const formTemplatesQueryVariables: FormChecklistTemplatesQueryVariables = {
    limit: Number(perPage),
    page: Number(page),
  };
  const filters: FormChecklistTemplateFilterInputObject[] = [];
  if (searchQuery) {
    filters.push(...createSearchFilters(searchQuery, defaultSearchListSchema));
  }
  if (filters.length) {
    formTemplatesQueryVariables.filters = filters;
  }
  if (orderingField && orderingDirection) {
    formTemplatesQueryVariables.sorts = {
      [orderingField]: orderingDirection,
    };
  }
  const formTemplatesQuery = useFormChecklistTemplatesQuery({
    fetchPolicy: 'cache-and-network',
    variables: formTemplatesQueryVariables,
  });
  const [fetchIdsQuery] = useFormChecklistTemplateIdsLazyQuery({
    fetchPolicy: 'no-cache',
  });
  const fetchIdsVariables = {
    filters: formTemplatesQueryVariables.filters,
    onlyIds: true,
  };
  const fetchIdsVariablesRef = useRef(fetchIdsVariables);
  fetchIdsVariablesRef.current = fetchIdsVariables;
  // Stable ref so fetchIds is safe as a useEffect dependency (no render loop).
  const fetchIds = useCallback(
    () => fetchIdsQuery({ variables: fetchIdsVariablesRef.current }),
    [fetchIdsQuery],
  );
  const { loading, data, previousData, refetch, error } = formTemplatesQuery;
  const queryData = data || previousData;
  const firstLoading = loading && previousData === undefined;
  const collection = _get(
    queryData,
    'data.collection',
    [],
  ) as FormChecklistTemplate[];
  const metadata = _get(queryData, 'data.metadata');
  const totalCount = _get(metadata, 'totalCount', 0);
  const totalPages = _get(metadata, 'totalPages', 1);
  return {
    fetchIds,
    collection,
    error,
    firstLoading,
    loading,
    refetch,
    totalCount,
    totalPages,
  };
}
